import { KEYCODE_MAP_HFD_LAYOUT } from "./keyCodeMapperHfd";
import {
  registerHfdFnDisplayName,
  resolveHfdFnFuncDisplayKeyCode,
} from "./hfdFnDisplay";

/** SDK FUNC 表有、本地 FUNC_MAP_HFD 未收录的 M98 Screen Fn 展示名 */
registerHfdFnDisplayName(161, "显示屏切换主页");
registerHfdFnDisplayName(166, "灯光颜色正向切换");
registerHfdFnDisplayName(167, "灯光颜色反向切换");

/** matrix (row,col) → Fn 层展示用 keyCode（对齐 M98 Screen 出厂 Fn 图 / 竞品驱动） */
const M98_SCREEN_FN_CELL_OVERRIDES: Readonly<Record<string, number>> = {
  // Row 0 — Esc
  "0,0": resolveHfdFnFuncDisplayKeyCode(4096, { v2: true }), // 恢复出厂设置
  // Row 1 — 数字行
  "1,11": resolveHfdFnFuncDisplayKeyCode(12293, { v2: true }), // - 灯光速度-
  "1,12": resolveHfdFnFuncDisplayKeyCode(12292, { v2: true }), // = 灯光速度+
  "1,13": resolveHfdFnFuncDisplayKeyCode(7, { v2: false }), // Backspace 查询电量
  // Row 2 — Tab / QWER … \| [ ] 小键盘区
  "2,1": resolveHfdFnFuncDisplayKeyCode(8192, { v2: true }), // Q 蓝牙通道1
  "2,2": resolveHfdFnFuncDisplayKeyCode(8193, { v2: true }), // W 蓝牙通道2
  "2,3": resolveHfdFnFuncDisplayKeyCode(8194, { v2: true }), // E 蓝牙通道3
  "2,4": resolveHfdFnFuncDisplayKeyCode(8202, { v2: true }), // R 无线回连
  "2,13": resolveHfdFnFuncDisplayKeyCode(12288, { v2: true }), // \|（keycode 49）灯光效果切换
  // Row 3 — Caps / ASDF … Enter
  "3,1": resolveHfdFnFuncDisplayKeyCode(8, { v2: false }), // A WIN
  "3,2": resolveHfdFnFuncDisplayKeyCode(9, { v2: false }), // S MAC
  "3,12": resolveHfdFnFuncDisplayKeyCode(161, { v2: false }), // Enter 显示屏切换主页
  // Row 4 — Shift / ZXCV … ↑
  "4,1": resolveHfdFnFuncDisplayKeyCode(63, { v2: false }), // Z GIF模式
  "4,2": resolveHfdFnFuncDisplayKeyCode(73, { v2: false }), // X 开启/关闭极致省电
  "4,12": resolveHfdFnFuncDisplayKeyCode(12290, { v2: true }), // ↑ 灯光亮度+
  // Row 5 — 底行 / 方向键
  "5,1": resolveHfdFnFuncDisplayKeyCode(22, { v2: false }), // L-Win 锁定Win
  "5,6": resolveHfdFnFuncDisplayKeyCode(12294, { v2: true }), // Space 关闭灯光
  "5,13": resolveHfdFnFuncDisplayKeyCode(167, { v2: false }), // ← 灯光颜色反向
  "5,14": resolveHfdFnFuncDisplayKeyCode(12291, { v2: true }), // ↓ 灯光亮度-
  "5,15": resolveHfdFnFuncDisplayKeyCode(166, { v2: false }), // → 灯光颜色正向
};

const M98_SCREEN_BASE_MATRIX = KEYCODE_MAP_HFD_LAYOUT.M98TFT;

function readM98ScreenEquipmentFlag(): boolean {
  try {
    const raw = localStorage.getItem("equipmentData");
    if (!raw) return false;
    const eq = JSON.parse(raw) as {
      displayname?: string;
      productName?: string;
      keyboardType?: string;
    };
    const name = String(eq?.displayname ?? eq?.productName ?? "").trim();
    return name === "M98 Screen" || String(eq?.keyboardType ?? "") === "M98TFT";
  } catch {
    return false;
  }
}

export function buildHuafendaM98ScreenFnLayerKeyCodes(): number[][] {
  return M98_SCREEN_BASE_MATRIX.map((row, rowIndex) =>
    row.map((baseCell, col) => {
      const overrideKey = `${rowIndex},${col}`;
      if (
        Object.prototype.hasOwnProperty.call(
          M98_SCREEN_FN_CELL_OVERRIDES,
          overrideKey
        )
      ) {
        return M98_SCREEN_FN_CELL_OVERRIDES[overrideKey];
      }
      if (baseCell === 0) return 0;
      return baseCell;
    })
  );
}

export function applyHuafendaM98ScreenFnLayerToSdkRows(
  rows: { layer: number; row: number; keyboardLayout: number[] }[]
): { layer: number; row: number; keyboardLayout: number[] }[] {
  const fnMatrix = buildHuafendaM98ScreenFnLayerKeyCodes();
  return rows.map((row) => {
    const staticRow = fnMatrix[row.row];
    if (!staticRow) return row;
    return { ...row, keyboardLayout: [...staticRow] };
  });
}

/** M98 Screen（M98TFT）Fn 层使用静态出厂映射表 */
export function shouldApplyHuafendaM98ScreenFnLayer(
  keyboardType: string,
  layer: number
): boolean {
  if (layer === 0) return false;
  if (keyboardType === "M98TFT") return true;
  return readM98ScreenEquipmentFlag();
}

export function getHuafendaM98ScreenFnLayerRow(
  rowIndex: number
): number[] | null {
  const matrix = buildHuafendaM98ScreenFnLayerKeyCodes();
  return matrix[rowIndex] ?? null;
}
